/* eslint-disable @typescript-eslint/no-unused-vars */
import DeleteIcon from "@mui/icons-material/Delete";
import InfoIcon from "@mui/icons-material/Info";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useSlugValidation } from "@platformx/authoring-apis";
import {
  RootState,
  updateContentForCard,
  updatePageModel,
  updatePageSettings,
} from "@platformx/authoring-state";
import {
  BasicSwitch,
  formatPageUrl,
  getContentUrlPath,
  getSelectedBrandName,
  getSubDomain,
  ShowToastError,
  ShowToastSuccess,
  SlugField,
  usePlatformAnalytics,
} from "@platformx/utilities";
import { useEffect, useRef, useState } from "react";

import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import BackButton from "../BackButton/BackButton";
import { descriptionLength, nameLength } from "../utils/constants";
import { PageSeoInformation } from "../utils/editTypes";
import PageSEO from "./PageSEO";
import "./PageSettings.css";
import { useCustomStyle } from "./SEOBasics.style";

const SEOBasics = ({ setPageId }) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const { page } = useSelector((state: RootState) => state);
  const { SeoTitle, SeoDescription, SeoKeywords, PageName, PageDescription, PageTags, PageURL } =
    page.pageSettings;
  const { SeoEnable } = page.pageModel;
  const initialStructureData = useRef<any>(JSON.parse(page.pageModel.StructureData));
  const [structureData, setStructureData] = useState(initialStructureData.current);
  const initialSeoInfo = useRef<PageSeoInformation>({
    SeoTitle: page?.pageSettings?.PageName || "",
    SeoDescription: page?.pageSettings?.PageDescription || "",
    SeoKeywords: page?.pageSettings?.PageTags || [],
    PageURL: PageURL || "",
    SeoBlockIndexing: true,
  });
  const [seoInfo, setSeoInfo] = useState<PageSeoInformation>(initialSeoInfo.current);
  const [handleImpression] = usePlatformAnalytics();
  const classes = useCustomStyle();
  // Must match CreateNewPage.tsx's PAGE_DOC_TYPE casing exactly — the backend
  // uniqueness check is case-sensitive on this value.
  const slugValidation = useSlugValidation("Sitepage");
  const initialPageSlug = useRef<string>(page?.pageModel?.slug || "");
  const [pageSlug, setPageSlug] = useState<string>(initialPageSlug.current);
  useEffect(() => {
    if (page?.pageSettings) {
      initialSeoInfo.current = {
        SeoTitle: SeoTitle !== undefined ? SeoTitle : PageName === undefined ? "" : PageName, //`${PageName} | ${siteLevelSchema.siteName}`,
        SeoDescription:
          SeoDescription !== undefined
            ? SeoDescription
            : PageDescription === undefined
              ? ""
              : PageDescription,
        SeoKeywords:
          SeoKeywords !== undefined &&
          SeoKeywords !== "" &&
          SeoKeywords?.length !== 0 &&
          SeoKeywords?.[0] !== ""
            ? [...page.pageSettings.SeoKeywords]
            : PageTags?.length === 0
              ? []
              : [...PageTags],
        PageURL:
          PageURL ||
          `${getSubDomain()}/${formatPageUrl(getSelectedBrandName() || i18n.language)}/${page?.pageModel.Page}`,
        SeoBlockIndexing: SeoEnable, //!== undefined ? SeoEnable : true,
      };
      setSeoInfo(initialSeoInfo.current);
      initialPageSlug.current = page?.pageModel?.slug || "";
      setPageSlug(initialPageSlug.current);
      setStructureData({
        ...structureData,
        title: initialSeoInfo.current?.SeoTitle,
        name: initialSeoInfo.current?.SeoTitle,
        description: initialSeoInfo.current?.SeoDescription,
        keywords: initialSeoInfo.current?.SeoKeywords?.join(",") || "",
        url: initialSeoInfo.current?.PageURL,
      });
    }
  }, [page?.pageSettings]);
  const updateStructureData = () => {
    setStructureData({
      ...structureData,
      name: seoInfo?.SeoTitle,
      description: seoInfo?.SeoDescription || "",
      keywords: seoInfo?.SeoKeywords?.join(",") || "",
    });
  };
  // Function to handle input field changes
  const handleDataChange = (event, fieldType) => {
    setSeoInfo({ ...seoInfo, [fieldType]: event.target.value });
  };
  // Function to handle switch changes
  const handleControlsChange = (event) => {
    setSeoInfo({ ...seoInfo, SeoBlockIndexing: event.target.checked });
  };

  useEffect(() => {
    if (slugValidation.isSubmitCheck) return;
    if (slugValidation.status === "unique") {
      ShowToastSuccess(t("slug_available", { docType: "Page" }));
    } else if (slugValidation.status === "not_unique") {
      ShowToastError(t("slug_not_unique", { docType: "Page" }));
    }
  }, [slugValidation.status, slugValidation.isSubmitCheck]);

  const handleSlugChange = (event) => {
    setPageSlug(event.target.value);
    slugValidation.invalidate();
  };

  const handleSlugBlur = () => {
    slugValidation.validate(pageSlug, { excludeValue: initialPageSlug.current });
  };

  const slugField = {
    value: pageSlug,
    onChange: handleSlugChange,
    onBlur: handleSlugBlur,
    canonicalUrl: pageSlug
      ? `${getSubDomain()}/${i18n.language}${getContentUrlPath({ tagName: "sitepage", slug: pageSlug })}`
      : "",
  };

  const saveSeoBasics = async () => {
    if (!pageSlug) {
      ShowToastError(t("slug_required"));
      return;
    }
    const isUnique = await slugValidation.checkNow(pageSlug, {
      excludeValue: initialPageSlug.current,
    });
    if (!isUnique) {
      ShowToastError(t("slug_url_not_unique", { docType: "Page" }));
      return;
    }
    // Refs aren't subject to the read/write races this rule guards against;
    // pageSlug was already captured (as the excludeValue check above) before
    // this await.
    // eslint-disable-next-line require-atomic-updates
    initialPageSlug.current = pageSlug;
    // dispatch(updateSeoEnable({ SeoEnable: seoInfo.SeoBlockIndexing }));
    page?.prelemMetaArray?.map((item, index) => {
      dispatch(
        updateContentForCard({
          selectedPrelemIndex: index,
          sectionToUpdate: "SeoEnabled",
          data: seoInfo.SeoBlockIndexing,
          index: undefined,
        }),
      );
    });
    dispatch(updatePageSettings({ pageInfo: seoInfo }));
    const pageModelNew = {
      ...page?.pageModel,
      SeoEnable: seoInfo.SeoBlockIndexing,
      slug: pageSlug,
    };
    pageModelNew.StructureData = JSON.stringify(structureData);
    dispatch(updatePageModel({ pageModelCreated: pageModelNew }));
    const pageDataObj = {
      eventType: "SEOBasics PageSetting Saved",
      SeoBasicsSaved: true,
    };
    handleImpression(pageDataObj.eventType, pageDataObj);
    ShowToastSuccess(`${t("seo_info_toast")} ${t("saved_toast")}`);
  };

  return (
    <Box className='pageSettingmainWp'>
      <BackButton setPageId={setPageId} Title={t("page_seo_basic")} />
      {/* <Box className='rowBox'>
        <Typography className='labelbox' variant='p4regular'>
          {t("page_search_preview")}
        </Typography>
        <Box className={`${classes.seoBasicsWrapper} seoBasics`}>
          <Typography variant='p4regular'>{seoInfo.PageURL}</Typography>
          <Typography variant='p4regular'>
            {seoInfo.SeoTitle.substring(0, previewNameLength)}
            {seoInfo.SeoTitle.length > previewNameLength && <span>...</span>}
          </Typography>
          <Typography variant='p4regular'>{seoInfo.SeoDescription}</Typography>
          <Typography variant='p4regular'>{seoInfo.SeoKeywords}</Typography>
        </Box>
      </Box> */}
      <Box className='rowBox'>
        <Typography className='labelbox' variant='p4regular'>
          {`${t("name")}`}
        </Typography>
        <TextField
          size='small'
          variant='filled'
          fullWidth
          multiline
          value={seoInfo.SeoTitle}
          onChange={(e) => handleDataChange(e, "SeoTitle")}
          onBlur={updateStructureData}
          placeholder={t("page_search_title_placeholder")}
          inputProps={{ maxLength: nameLength }}
        />
      </Box>
      <Box className='rowBox'>
        <Typography className='labelbox' variant='p4regular'>
          {`${t("description")}`}
        </Typography>
        <TextField
          size='small'
          variant='filled'
          fullWidth
          multiline
          value={seoInfo.SeoDescription}
          onChange={(e) => handleDataChange(e, "SeoDescription")}
          onBlur={updateStructureData}
          placeholder={t("page_search_description_placeholder")}
          inputProps={{ maxLength: descriptionLength }}
        />
      </Box>
      <Box className='rowBox'>
        <Typography className='labelbox' variant='p4regular'>
          {`${t("Keywords")}`}
          <span style={{ position: "relative" }}>
            <Tooltip title='After each tag, hit enter to add it as a keyword.' arrow>
              <InfoIcon
                fontSize='small'
                style={{
                  marginLeft: "8px",
                  cursor: "pointer",
                  height: "18px",
                  width: "18px",
                  position: "absolute",
                  bottom: "0px",
                }}
              />
            </Tooltip>
          </span>
        </Typography>
        <Autocomplete
          multiple
          id='tags-filled'
          value={[...seoInfo.SeoKeywords]}
          options={[]}
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          onChange={(event: object, value) => {
            const result = value.filter((str) => str.trim().length !== 0);
            setSeoInfo({ ...seoInfo, SeoKeywords: result });
          }}
          onBlur={updateStructureData}
          freeSolo
          renderTags={(value: string[], getTagProps) =>
            value.map(
              (option: string, index: number) =>
                option && (
                  <Box key={index} mt={1} ml={1}>
                    <Chip
                      variant='outlined'
                      label={option}
                      deleteIcon={<DeleteIcon sx={{ color: "#2d2d39" }} />}
                      sx={{
                        ".Platform-x-Chip-deleteIcon": {
                          color: "#000",
                        },
                        margin: "0px !important",
                      }}
                      {...getTagProps({ index })}
                    />
                  </Box>
                ),
            )
          }
          renderInput={(params) => (
            <TextField
              {...params}
              variant='filled' //placeholder={t("page_info_tags_placeholder")}
            />
          )}
          sx={{
            "& .Platform-x-InputBase-root": {
              flexWrap: "wrap",
              width: "auto",
            },
            ".Platform-x-Autocomplete-tag": {
              margin: "0 5px 5px 0",
            },
            "& .Platform-x-InputBase-input": {
              width: 0,
              minWidth: "150px",
            },
          }}
        />
      </Box>
      <Box className='rowBox'>
        <Typography className={`${classes.switchBoxContainer} switchbox`} variant='p4regular'>
          {t("page_search_seokey")}
          <BasicSwitch
            color={theme.palette.customColor.PRIMARY}
            bgcolor={theme.palette.customColor.PLACEHOLDER}
            onChange={(e: any) => {
              handleControlsChange(e);
              updateStructureData();
            }}
            checked={seoInfo.SeoBlockIndexing}
            basicSwitchRootClassName='switchRootClass'
          />
        </Typography>
      </Box>
      <Box className='rowBox'>
        <SlugField
          value={slugField.value}
          onChange={slugField.onChange}
          onBlur={slugField.onBlur}
          canonicalUrl={slugField.canonicalUrl}
          layout='column'
        />
      </Box>
      <Box className='rowBox'>
        <PageSEO
          // initialSeoInfo={initialSeoInfo}
          StructuredData={JSON.stringify(structureData)}
          setStructureData={setStructureData}
          seoInfoData={seoInfo}
        />
      </Box>
      <Box className='rowBox'>
        <Button
          variant='primaryButton'
          disabled={
            initialSeoInfo.current === seoInfo && initialStructureData.current === structureData
          }
          onClick={saveSeoBasics}
          data-testid='save-seo-basics'
          sx={{ width: "100%" }}>
          {t("done")}
        </Button>
      </Box>
    </Box>
  );
};
export default SEOBasics;
